import { useEffect, useState } from 'react';

import { Avatar, Badge, Button, Icon, IconButton } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import { dados } from '@/data/porta';
import type { AcessoDoMembro, EdicaoDeMembro, MembroDaEquipe } from '@/data/tipos';

const ACESSOS: { value: AcessoDoMembro; label: string }[] = [
    { value: 'ativo', label: 'Ativo' },
    { value: 'convite-pendente', label: 'Convite pendente' },
    { value: 'desativado', label: 'Desativado' },
];

/* O banco só aceita estes quatro (`membros.papel`, 0001). O campo era texto
   livre, e qualquer mudança nele voltava como erro do banco. */
const PAPEIS: { value: string; label: string }[] = [
    { value: 'profissional', label: 'Profissional' },
    { value: 'recepcao', label: 'Recepção' },
    { value: 'financeiro', label: 'Financeiro' },
];

function opcoesDePapel(atual: string): { value: string; label: string; disabled?: boolean }[] {
    if (PAPEIS.some((p) => p.value === atual)) return PAPEIS;
    /* Proprietária não se atribui por aqui, mas quem já é aparece como é. */
    return [{ value: atual, label: atual === 'proprietaria' ? 'Proprietária' : atual }, ...PAPEIS];
}

export interface DetalheDoMembroProps {
    membro: MembroDaEquipe | null;
    editando?: boolean;
    onClose: () => void;
    onSalvo: () => void;
    onVerAgenda: (membro: MembroDaEquipe) => void;
}

export function DetalheDoMembro({ membro, editando = false, onClose, onSalvo, onVerAgenda }: DetalheDoMembroProps) {
    const aviso = useAviso();
    const [confirmar, confirmacao] = useConfirmacao();
    const [edicao, setEdicao] = useState(editando);
    const [form, setForm] = useState<MembroDaEquipe | null>(membro);
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        setForm(membro);
        setEdicao(editando);
        setErro(null);
    }, [membro, editando]);

    if (!membro || !form) return null;

    const alterar = <C extends keyof MembroDaEquipe>(campo: C, valor: MembroDaEquipe[C]) => {
        setErro(null);
        setForm((f) => (f ? { ...f, [campo]: valor } : f));
    };
    const sujo = JSON.stringify(form) !== JSON.stringify(membro);

    const salvar = async (mudancas?: EdicaoDeMembro, titulo = 'Profissional atualizado'): Promise<boolean> => {
        const final = { ...form, ...mudancas };
        /* Nome vazio era ignorado pelo adaptador, e o aviso dizia "atualizado". */
        if (!final.nome.trim()) {
            setErro('O nome não pode ficar vazio.');
            return false;
        }
        setSalvando(true);
        try {
            await dados().atualizarMembro(membro.id, final);
            aviso({ title: titulo, description: membro.nome });
            onSalvo();
            onClose();
            return true;
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar.');
            return false;
        } finally {
            setSalvando(false);
        }
    };

    const desativar = async () => {
        const ok = await confirmar({
            titulo: `Desativar o acesso de ${membro.nome}?`,
            descricao: 'A pessoa deixa de entrar na Fórmula MED. O cadastro continua na lista, marcado como desativado.',
            confirmar: 'Desativar acesso',
        });
        if (ok) await salvar({ acesso: 'desativado' }, 'Acesso desativado');
    };

    /* O convite sai de verdade, pelo e-mail: o aviso só aparece depois de o
       servidor dizer para onde saiu. */
    const reenviarConvite = async () => {
        setSalvando(true);
        try {
            const r = await dados().convidarMembro(membro.id);
            aviso({
                title: 'Convite enviado',
                description: r.email ? `${membro.nome} · ${r.email}` : `${membro.nome} · na demonstração, nenhum e-mail sai`,
            });
            onClose();
        } catch (e) {
            aviso({ title: 'Não foi possível convidar', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    const pendente = membro.acesso === 'convite-pendente';

    return (
        <>
            <Modal
                open onClose={onClose} width={560} title={membro.nome} description={membro.papel}
                dirty={edicao && sujo} onSave={() => salvar()}
                footer={
                    <>
                        {/* Desativar acesso é destrutivo e fica à esquerda, longe do
                            botão principal — e desativado não some da lista, porque
                            a equipe precisa ver quem perdeu acesso. */}
                        <Button variant="ghost" disabled={salvando || membro.acesso === 'desativado'} onClick={() => void desativar()}>
                            Desativar acesso
                        </Button>
                        <span style={{ marginLeft: 'auto' }} />
                        <Button variant="secondary" iconLeft={<Icon name="calendar-days" size={15} />} onClick={() => onVerAgenda(membro)}>Ver agenda</Button>
                        {edicao
                            ? <Button onClick={() => void salvar()} loading={salvando} disabled={!sujo}>Salvar</Button>
                            : pendente
                                ? <Button loading={salvando} onClick={() => void reenviarConvite()}>Reenviar convite</Button>
                                : <Button onClick={() => setEdicao(true)}>Editar</Button>}
                    </>
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Avatar name={membro.nome} size="lg" />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                            <Badge tone={membro.acesso === 'ativo' ? 'success' : pendente ? 'warning' : 'neutral'} dot>
                                {ACESSOS.find((a) => a.value === membro.acesso)?.label}
                            </Badge>
                            <span style={{ fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-secondary)' }}>{membro.telefone}</span>
                        </div>
                        <span style={{ marginLeft: 'auto' }} />
                        {!edicao && <IconButton variant="outline" icon={<Icon name="pencil" size={15} />} label="Editar profissional" onClick={() => setEdicao(true)} />}
                    </div>

                    {edicao ? (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                            <FormField label="Nome" required><Input aria-label="Nome" value={form.nome} onChange={(v) => alterar('nome', v)} invalid={!form.nome.trim() && !!erro} /></FormField>
                            <FormField label="Telefone"><Input aria-label="Telefone" value={form.telefone} onChange={(v) => alterar('telefone', v)} /></FormField>
                            <FormField label="Papel"><Select aria-label="Papel" value={form.papel} onChange={(v) => alterar('papel', v)} options={opcoesDePapel(membro.papel)} /></FormField>
                            <FormField label="Registro no conselho"><Input aria-label="Registro no conselho" value={form.registro ?? ''} onChange={(v) => alterar('registro', v)} placeholder="CRM-SP 000.000" /></FormField>
                            <FormField label="Especialidades" style={{ gridColumn: '1 / -1' }}>
                                <Input aria-label="Especialidades" value={form.especialidades} onChange={(v) => alterar('especialidades', v)} />
                            </FormField>
                            <FormField label="Acesso">
                                <Select aria-label="Acesso" value={form.acesso} onChange={(v) => alterar('acesso', v as AcessoDoMembro)} options={ACESSOS} />
                            </FormField>
                        </div>
                    ) : (
                        <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '9px 16px', fontFamily: 'var(--font-ui)', fontSize: 13 }}>
                            {([
                                ['Função', membro.papel],
                                ['Registro', membro.registro ?? '—'],
                                ['Especialidades', membro.especialidades],
                                ['Carga semanal', membro.cargaSemanal ?? '—'],
                            ] as const).map(([rotulo, valor]) => (
                                <div key={rotulo} style={{ display: 'contents' }}>
                                    <dt style={{ color: 'var(--text-secondary)' }}>{rotulo}</dt>
                                    <dd style={{ margin: 0, textAlign: 'right', fontWeight: 600, color: 'var(--text-primary)' }}>{valor}</dd>
                                </div>
                            ))}
                        </dl>
                    )}
                </div>
            </Modal>
            {confirmacao}
        </>
    );
}
